import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@mui/material';

import { PaymentFormEnum, SpecialNeedsEnum } from '@mopla/data-models';

import { PassengerBookingInfo } from '../../atoms/PassengerBookingInfo';
import { ArrowDownIcon, CheckmarkIcon } from '../../icons';

import { AdditionalNeeds } from './AdditionalNeeds';
import {
	ExpandIconWrapper,
	ManualButton,
	PaymentFormWrapper,
	Root,
} from './PassengerCheckCard.styles';

export interface PassengerCheckCardProps {
	name: string;
	id: string;
	attendees: number;
	bookingCode?: string;
	onManualClick?: VoidFunction;
	onCancelClick?: VoidFunction;
	disabled?: boolean;
	disabledText?: boolean;
	paymentForm?: PaymentFormEnum;
	isCancelledGuest?: boolean;
	specialNeeds?: SpecialNeedsEnum;
	bulkyLuggage?: boolean;
	showAdditionalNeeds?: boolean;
	testId?: string;
}

export const PassengerCheckCard: FC<PassengerCheckCardProps> = ({
	name,
	id,
	bookingCode,
	attendees,
	onManualClick,
	onCancelClick,
	disabled,
	disabledText,
	paymentForm,
	isCancelledGuest = false,
	showAdditionalNeeds = false,
	specialNeeds,
	bulkyLuggage,
	testId,
}) => {
	const [cancelledCollapsed, setCancelledCollapsed] = useState<boolean>(true);

	const handleToggleNoShowBlock = () =>
		setCancelledCollapsed((prevState) => !prevState);
	const collapsedNoShowBlock = isCancelledGuest && cancelledCollapsed;

	const { t } = useTranslation('checkIn');
	return (
		<Root disabled={disabled || collapsedNoShowBlock} data-testid={testId}>
			<PassengerBookingInfo
				bookingId={id}
				bookingCode={bookingCode}
				additionalPassengersCount={attendees}
				testId="checkIn-passenger-card-info"
				disabledText={disabledText}
				mainPassengerFullName={name}
			/>
			{showAdditionalNeeds && (
				<AdditionalNeeds
					specialNeeds={specialNeeds}
					bulkyLuggage={bulkyLuggage}
				/>
			)}
			{paymentForm && (
				<PaymentFormWrapper className="spaceBetween">
					<Typography variant="body2" color="primary.dark">
						{t('text.payment_method')}
					</Typography>
					<Typography variant="body2" fontWeight={600} color="primary.dark">
						{paymentForm === 'DIGITAL' && <CheckmarkIcon />}
						{t(`text.${paymentForm}`)}
					</Typography>
				</PaymentFormWrapper>
			)}
			{onManualClick &&
				(collapsedNoShowBlock ? (
					<ExpandIconWrapper onClick={handleToggleNoShowBlock}>
						<ArrowDownIcon />
					</ExpandIconWrapper>
				) : (
					<ManualButton
						data-testid="checkIn-passenger-card-button"
						onClick={onManualClick}
						variant="outlined"
					>
						{t('button.check_in')}
					</ManualButton>
				))}
			{onCancelClick && (
				<ManualButton
					data-testid="checkIn-passenger-card-no-show-button"
					onClick={onCancelClick}
					variant="outlined"
					red
				>
					{t('button.cancel_booking')}
				</ManualButton>
			)}
		</Root>
	);
};
